import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { DossierDocumentSign } from './types';
import DossierDocumentSignContext from './DossierDocumentSignContext';
import useDocumentSign from '../hooks/useDocumentSign';
import { useCurrentDossierCtx } from './CurrentDossierProvider';

const DossierDocumentSignProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const [dossiers, setDossiers] = useState<Array<DossierDocumentSign>>([]);
    const [raisableDossiers, setRaisableDossiers] = useState<Array<DossierDocumentSign>>([]);
    const [selectedDossier, setSelectedDossier] = useState<DossierDocumentSign>();
    const [loading, setLoading] = useState<boolean>(true);
    const { dossier: currentDossier } = useCurrentDossierCtx();
    const { getDossierSignMetadata, getDossiers, getRaisableDossiers } = useDocumentSign();

    const loadDossiers = useCallback(async () => {
        if (!currentDossier) {
            return;
        }
        setLoading(true);
        setDossiers([]);
        setRaisableDossiers([]);

        try {
            const metadataResponse = await getDossierSignMetadata(currentDossier.id as number);
            const dossiersData = await getDossiers(metadataResponse);
            setDossiers(dossiersData);
            const raisableDossiersData = await getRaisableDossiers(metadataResponse);
            setRaisableDossiers(raisableDossiersData);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [
        setLoading,
        setDossiers,
        setRaisableDossiers,
        getDossierSignMetadata,
        getRaisableDossiers,
        getDossiers,
        currentDossier,
    ]);

    const getDossiersBySignedFileId = useCallback(
        (fichierId: number): Array<DossierDocumentSign> =>
            dossiers.filter(
                (dossier) =>
                    dossier.documents.find(
                        (document) => document.fileId === fichierId && document.signed,
                    ) !== undefined,
            ),
        [dossiers],
    );

    const isRaisable = useCallback(
        (id: number): boolean => raisableDossiers.find((item) => item.id === id) !== undefined,
        [raisableDossiers],
    );

    useEffect(() => {
        if (currentDossier) {
            loadDossiers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDossier]);

    const contextValue = useMemo(
        () => ({
            dossiers,
            selectedDossier,
            loading,
            loadDossiers,
            setSelectedDossier,
            isRaisable,
            getDossiersBySignedFileId,
        }),
        [dossiers, selectedDossier, loading, loadDossiers, isRaisable, getDossiersBySignedFileId],
    );

    return (
        <DossierDocumentSignContext.Provider value={contextValue}>
            {children}
        </DossierDocumentSignContext.Provider>
    );
};

export default DossierDocumentSignProvider;
